import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

// Interfaces pour typer les réponses
export interface DashboardKPIs {
  commissions_this_month: number;
  conversion_rate: number;
  average_basket: number;
  monthly_target_completion: number;
  projects_count: number;
  total_amount: number;
}

export interface CommissionEvolution {
  month: string;
  commission: number;
  projects: number;
  amount: number;
}

export interface TopInsurer {
  name: string;
  projects: number;
  amount: number;
  commission: number;
}

export interface ProjectToFollow {
  id: number;
  project_id: string;
  name: string;
  company: string;
  amount: number;
  created_at: string;
  action_needed: string;
  days_since_creation: number;
}

export interface RecentActivity {
  id: number;
  type: string;
  description: string;
  date: string;
  icon: string;
  color: string;
}

export interface PipelineStats {
  prospect: number;
  quote_sent: number;
  negotiation: number;
  signed: number;
  lost: number;
}

export interface UserPerformance {
  user_id: number;
  name: string;
  email: string;
  total_projects: number;
  total_amount: number;
  estimated_commission: number;
  is_current_user: boolean;
}

export interface AdminUserProjectStats {
  user_id: number;
  name: string;
  email: string;
  total_projects: number;
  projects_treated: number;
  projects_to_process: number;
  treatment_rate: number;
  total_amount: number;
}

// ====== Vue d'ensemble (nouveau dashboard) ======
export interface OverviewScope {
  user_id: number | null;
  is_team_view: boolean;
  is_admin: boolean;
}

export interface OverviewKpis {
  projects_total: number;
  projects_this_month: number;
  projects_last_month: number;
  evolution_projects: number;
  amount_total: number;
  amount_this_month: number;
  amount_last_month: number;
  evolution_amount: number;
  revenu_total: number;
  revenu_this_month: number;
  revenu_last_month: number;
  evolution_revenu: number;
  average_basket: number;
  won: number;
  lost: number;
  active_projects: number;
  conversion_rate: number;
  devis_total: number;
  devis_this_month: number;
  treated: number;
  to_process: number;
  treatment_rate: number;
  rank: number | null;
  total_users: number | null;
}

export interface PipelinePhase {
  phase: string;
  count: number;
  amount: number;
}

export interface MonthlyPoint {
  month: string;
  projects: number;
  amount: number;
  revenu: number;
  won: number;
}

export interface DistributionItem {
  label: string;
  count: number;
  amount: number;
  won?: number;
}

export interface TopAssureurOverview {
  name: string;
  projects: number;
  amount: number;
  revenu?: number;
}

export interface RelanceItem {
  id: number;
  reference: string;
  client: string;
  phase: string;
  amount: number;
  nb_appels: number;
  days_in_phase: number;
  date_envoi_devis: string | null;
}

export interface DashboardOverview {
  scope: OverviewScope;
  kpis: OverviewKpis;
  pipeline: PipelinePhase[];
  monthly_evolution: MonthlyPoint[];
  by_origine: DistributionItem[];
  by_objet_pret: DistributionItem[];
  top_assureurs: TopAssureurOverview[];
  top_banques: TopAssureurOverview[];
  relances: RelanceItem[];
}

export interface TeamMemberStats {
  user_id: number;
  name: string;
  email: string;
  total_projects: number;
  projects_this_month: number;
  total_amount: number;
  total_revenu: number;
  devis_envoyes: number;
  won: number;
  lost: number;
  conversion_rate: number;
  treated: number;
  to_process: number;
  treatment_rate: number;
  is_current_user: boolean;
}

export interface CurrentUserStats {
  projects_this_month: number;
  projects_last_month: number;
  evolution_projects: number;
  amount_this_month: number;
  amount_last_month: number;
  evolution_amount: number;
  rank: number;
  total_users: number;
  commission_this_month: number;
  projects_treated: number;
  projects_to_process: number;
}

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  private apiUrl = `${environment.apiUrl}/api/dashboard`;

  constructor(private http: HttpClient) { }

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  private handleError(error: any): Observable<never> {
    console.error('Dashboard API Error:', error);
    let errorMessage = 'Une erreur est survenue';
    
    if (error.error instanceof ErrorEvent) {
      // Erreur côté client
      errorMessage = error.error.message;
    } else {
      // Erreur côté serveur
      errorMessage = error.error?.detail || error.message || errorMessage;
    }
    
    return throwError(() => new Error(errorMessage));
  }

  // Récupérer les KPIs essentiels
  getKPIs(): Observable<DashboardKPIs> {
    return this.http.get<DashboardKPIs>(`${this.apiUrl}/kpis`, {
      headers: this.getHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Récupérer l'évolution des commissions
  getCommissionsEvolution(months: number = 6): Observable<CommissionEvolution[]> {
    return this.http.get<CommissionEvolution[]>(`${this.apiUrl}/commissions-evolution`, {
      headers: this.getHeaders(),
      params: { months: months.toString() }
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Récupérer le top des assureurs
  getTopInsurers(limit: number = 5): Observable<TopInsurer[]> {
    return this.http.get<TopInsurer[]>(`${this.apiUrl}/top-insurers`, {
      headers: this.getHeaders(),
      params: { limit: limit.toString() }
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Récupérer les projets à suivre
  getProjectsToFollow(limit: number = 10): Observable<ProjectToFollow[]> {
    return this.http.get<ProjectToFollow[]>(`${this.apiUrl}/projects-to-follow`, {
      headers: this.getHeaders(),
      params: { limit: limit.toString() }
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Récupérer les activités récentes
  getRecentActivity(limit: number = 10): Observable<RecentActivity[]> {
    return this.http.get<RecentActivity[]>(`${this.apiUrl}/recent-activity`, {
      headers: this.getHeaders(),
      params: { limit: limit.toString() }
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Récupérer les stats du pipeline
  getPipelineStats(): Observable<PipelineStats> {
    return this.http.get<PipelineStats>(`${this.apiUrl}/pipeline-stats`, {
      headers: this.getHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Récupérer la performance de tous les utilisateurs
  getUsersPerformance(): Observable<UserPerformance[]> {
    return this.http.get<UserPerformance[]>(`${this.apiUrl}/users-performance`, {
      headers: this.getHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Récupérer les stats de traitement de tous les utilisateurs (admin seulement)
  getAdminUsersStatusStats(): Observable<AdminUserProjectStats[]> {
    return this.http.get<AdminUserProjectStats[]>(`${this.apiUrl}/admin/users-status-stats`, {
      headers: this.getHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Vue d'ensemble complète (scope géré côté backend selon le rôle)
  getOverview(userId?: number | null, months: number = 12): Observable<DashboardOverview> {
    const params: { [key: string]: string } = { months: months.toString() };
    if (userId !== null && userId !== undefined) {
      params['user_id'] = userId.toString();
    }
    return this.http.get<DashboardOverview>(`${this.apiUrl}/overview`, {
      headers: this.getHeaders(),
      params
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Tableau de performance par utilisateur (admin uniquement)
  getTeamOverview(): Observable<TeamMemberStats[]> {
    return this.http.get<TeamMemberStats[]>(`${this.apiUrl}/admin/team-overview`, {
      headers: this.getHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Récupérer les statistiques détaillées de l'utilisateur courant
  getCurrentUserStats(): Observable<CurrentUserStats> {
    return this.http.get<CurrentUserStats>(`${this.apiUrl}/current-user-stats`, {
      headers: this.getHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }
}
